import { PageController } from '@client/components/_core/layout/PageController';
import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { usePathState } from '@client/hooks/navigation/usePathState';
import { createColumnHelper, PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import type { RouterInputs, RouterOutputs } from '@client/trpc/client';
import { DEFAULT_PAGE_SIZE } from '@shared/constants';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { Space, Tag } from 'antd';
import { UploadIcon, Users } from 'lucide-react';
import { useState } from 'react';
import { HandlingPartyFilters } from './HandlingPartyFilters';
import { ImportHandlingPartiesModal } from './ImportHandlingPartiesModal';

export const Route = createFileRoute('/console/$orgId/configuration/org-settings/handling-parties')({
    staticData: {
        metadata: {
            title: 'Handling parties',
        },
        ui: {
            icon: Users,
            title: 'Handling parties configuration',
            subtitle: 'Manage your handling parties and their status',
            menuDescription: 'Configure claim handling parties',
        },
        // Must equal HANDLING_PARTY_MODULES (shared) — kept as a literal because the route-registry
        // generator reads staticData statically; the shared registry test enforces the pairing (CUR-1145).
        modules: ['claim', 'compliance', 'risk'],
        permissions: ['settings:update'],
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <ConfigurationHandlingParties />
        </PageController>
    );
}

type QueryParams = RouterInputs['handlingParty']['listHandlingParties'];

const columnHelper = createColumnHelper<
    NonNullable<RouterOutputs['handlingParty']['listHandlingParties']>['rows'][number],
    NonNullable<QueryParams['orderBy']>
>();

function ConfigurationHandlingParties() {
    const orgId = useOrgId();
    const { trpc } = useTrpc();
    const { mutate, isPending: isPendingDeleteHandlingParty } = trpc.handlingParty.deleteHandlingParty.useMutation();
    const [input, setInput] = usePathState<QueryParams>('query-params', {
        limit: DEFAULT_PAGE_SIZE,
        orderBy: { id: 'desc' },
        where: {},
    });
    const {
        data,
        isPending: isPendingListHandlingParties,
        refetch,
    } = trpc.handlingParty.listHandlingParties.useQuery(input);
    const navigate = useNavigate();

    const [importModalOpen, setImportModalOpen] = useState(false);

    const tableProps = usePrismaTable({
        rowKey: 'id',
        data: data?.rows || [],
        total: data?.total || 0,
        loading: isPendingListHandlingParties || isPendingDeleteHandlingParty,
        queryParams: input,
        onParamsChange: setInput,
        columns: [
            columnHelper.accessor('id', {
                id: 'id',
                header: 'ID',
                sortable: 'id',
                cell: ({ value }) => String(value),
                align: 'center',
                width: 70,
            }),
            columnHelper.accessor('name', {
                id: 'name',
                header: 'Name',
                sortable: 'name',
                cell: ({ value }) => value,
            }),
            columnHelper.accessor('membersCount', {
                id: 'membersCount',
                header: 'Members',
                sortable: 'UsersInHandlingParty._count',
                cell: ({ value }) => String(value),
            }),
            columnHelper.accessor('isDisabled', {
                id: 'isDisabled',
                header: 'Status',
                sortable: 'isDisabled',
                cell: ({ value }) => (
                    <Tag variant="outlined" color={value ? 'red' : 'green'}>
                        {value ? 'Disabled' : 'Active'}
                    </Tag>
                ),
            }),
            columnHelper.actions({
                width: 50,
                cell: ({ row }) => (
                    <Space>
                        <Button.OpenInNewTab
                            to="/console/$orgId/configuration/org-settings/handling-parties/details/$id"
                            params={{ orgId, id: row.id }}
                        />
                        <Button.Delete
                            onConfirm={(callback) =>
                                mutate(
                                    { id: row.id },
                                    {
                                        onSuccess() {
                                            callback();
                                            void refetch();
                                        },
                                        onError(error) {
                                            errorMessage.show(error);
                                        },
                                    },
                                )
                            }
                        />
                    </Space>
                ),
            }),
        ],
    });

    return (
        <>
            <Outlet />

            <ImportHandlingPartiesModal
                open={importModalOpen}
                onClose={() => setImportModalOpen(false)}
                onSuccess={() => void refetch()}
            />

            <TableToolbar
                filters={<HandlingPartyFilters onChange={(where) => setInput((prev) => ({ ...prev, where }))} />}
                actions={
                    <>
                        <Button
                            icon={<UploadIcon className="size-4" />}
                            onClick={() => setImportModalOpen(true)}
                            type="secondary"
                        >
                            Bulk Import
                        </Button>
                        <Button.Add.Link
                            label="New handling party"
                            to="/console/$orgId/configuration/org-settings/handling-parties/add"
                            params={{ orgId }}
                            variant="primary"
                        />
                    </>
                }
            />

            <PrismaTable
                {...tableProps}
                onRow={({ id }) => ({
                    className: 'cursor-pointer',
                    onClick(e) {
                        navigate(
                            {
                                to: '/console/$orgId/configuration/org-settings/handling-parties/details/$id',
                                params: { orgId, id },
                            },
                            e,
                        );
                    },
                })}
            />
        </>
    );
}
